<template>
  <Carousel
    v-if="eyecatches.length > 0"
    v-bind="carouselConfig"
    :mouse-drag="isDisplayingMultipleImages"
    :touch-drag="isDisplayingMultipleImages"
    @init="initSlide"
    @slide-end="slideEnd"
  >
    <Slide v-for="(eyecatch, index) in eyecatches" :key="index" :style="slidesStyle[index]">
      <div class="carousel__item">
        <common-image
          :image-object="eyecatch"
          :alt="eyecatch.alt"
          :is-fixed-dimension="true"
          :is-eyecatch="true"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          class="eyecatch"
          @mounted="(url: string) => handleImageMounted(index, url)"
        />

        <div v-if="checkMjSelect(event) && index === 0" class="img-stamp">
          <common-lazy-load-image
            :image-url="'/img/stamp_mj-select.svg'"
            :image-object="{ width: 60, height: 60 }"
            :is-fixed-dimension="true"
            alt="mj-select"
            background-color="none"
          />
        </div>
      </div>
    </Slide>
    <template #addons="{ currentSlide, slidesCount }">
      <template v-if="isDisplayingMultipleImages">
        <Navigation />
        <div class="pagination">{{ `${clamp(currentSlide + 1, 1, slidesCount)}/${slidesCount}` }}</div>
      </template>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="bannerStamp" :class="bannerStamp.class" class="banner-stamp" v-html="sanitizeXssHtml(bannerStamp.text)"></div>
    </template>
  </Carousel>
</template>

<script setup lang="ts">
import { Slide, Navigation, Carousel } from 'vue3-carousel/dist/carousel.es.js'
import 'vue3-carousel/dist/carousel.css'
import sanitizeXssHtml from 'xss'
import { useMainStore } from '@/store'
import GTM from '@/utils/tracking/gtm'
import { EVENT_DETAIL_CAROUSEL_BANNER_VIEW } from '@/utils/constants/gaCustomEventSet'

const FIRST_VIEW_BANNER_INDEX = 0

interface Image {
  alt: string
  sp_url: string
  url: string
  sp_width?: number
  sp_height?: number
  width: number
  height: number
}

interface Content {
  category_text: string
  sections?: Section[]
}

interface Section {
  image: Image
}

interface FeatureTag {
  slug: string
}

interface Event {
  event_eyecatches?: Image[]
  detail?: {
    contents?: Content[]
  }
  use_content_image_as_eyecatch: boolean
  feature_tags?: FeatureTag[]
  eyecatch_image: Image
}

interface Props {
  event: Event
  bannerStamp?: {
    class: string[]
    text: string
  }
}

interface SlideEndProps {
  currentSlideIndex: number
  prevSlideIndex: number
  slidesCount: number
}

const props = defineProps<Props>()

function handleImageMounted(index: number, url: string) {
  const webpUrl = getWebpImageSrc(url) || url
  slidesStyle.value[index].backgroundImage = `url(${webpUrl})`
}

function clamp(value: number, min: number, max: number) {
  if (value > max) return max
  if (value < min) return min
  return value
}

function preloadMainImage(images: Image[]) {
  if (images.length === 0) return
  const mainImageUrl = getWebpImageSrc(store.isSP && images[0].sp_url ? images[0].sp_url : images[0].url)
  useHead({
    link: [
      {
        rel: 'preload',
        href: mainImageUrl,
        as: 'image',
        type: mainImageUrl.endsWith('.webp') ? 'image/webp' : 'image/jpeg',
        fetchpriority: 'high'
      }
    ]
  })
}

function checkMjSelect(event: Event) {
  if (!event) return
  return event.feature_tags && event.feature_tags.find(tag => tag.slug === 'mj_select')
}

const eyecatches = computed<Image[]>(() => {
  if (props.event.event_eyecatches && props.event.event_eyecatches.length > 0) {
    return [props.event.eyecatch_image].concat(props.event.event_eyecatches)
  }

  if (props.event.use_content_image_as_eyecatch && props.event.detail?.contents) {
    const contents = props.event.detail.contents.find(content => content.category_text === 'ポイント')
    const content_images = contents?.sections?.map(section => section.image).filter(Boolean) || []
    return [props.event.eyecatch_image].concat(content_images)
  }

  return [props.event.eyecatch_image]
})

const slidesStyle = ref(eyecatches.value.map((_eyecatch: unknown) => ({ backgroundImage: 'none' })))

const isDisplayingMultipleImages = computed(() => eyecatches.value.length > 1)

const carouselConfig = {
  itemsToShow: 1,
  wrapAround: true
}

const store = useMainStore()
const mainEyecatch = computed(() => props.event.eyecatch_image)
const width = computed(() => (store.isSP && mainEyecatch.value.sp_width ? mainEyecatch.value.sp_width : mainEyecatch.value.width))
const height = computed(() => (store.isSP && mainEyecatch.value.sp_height ? mainEyecatch.value.sp_height : mainEyecatch.value.height))

const slideGTMEventExecute = (index: number) => {
  GTM.sendEventToGa({ ...EVENT_DETAIL_CAROUSEL_BANNER_VIEW, label: { view_banner: index + 1, max_banner: eyecatches.value.length } })
}
const initSlide = () => {
  slideGTMEventExecute(FIRST_VIEW_BANNER_INDEX)
}
const slideEnd = (data: SlideEndProps) => {
  slideGTMEventExecute(data.currentSlideIndex)
}

if (eyecatches.value.length > 0) preloadMainImage(eyecatches.value)
</script>

<style lang="scss" scoped>
.pagination {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  padding-left: 6px;
  padding-right: 6px;
  background-color: $black-1a;
  opacity: 70%;
  color: white;
  border-radius: 40px;
}

.carousel {
  --background-base-color: #{$black-1a};
  --vc-nav-color: white;
  --vc-nav-background: rgba(from var(--background-base-color) r g b / 70%);
  --vc-nav-height: 64px;
  --vc-nav-color-hover: #bfbfbf;
  --vc-nav-width: 23px;
}

.carousel__slide {
  background-size: cover;
  aspect-ratio: v-bind(width) / calc(v-bind(height) * 1.3);
}

:deep(.carousel__prev),
:deep(.carousel__next) {
  margin: 0;
}

.carousel__item {
  width: 100%;
  height: 100%;
  backdrop-filter: blur(20px);
  align-content: center;
  background-color: #d1d1d1b2;
}

:deep(.eyecatch) {
  height: 100%;
  width: 100%;
  object-fit: contain;
}

.banner-stamp {
  position: absolute;
  font-weight: bold;
  color: white;
  padding-left: 16px;
  padding-right: 16px;
  top: 0;

  @include sp {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &.orange {
    background-color: $orange;
  }
}

.img-stamp {
  position: absolute;
  bottom: 10px;
  left: 4px;
  width: 60px;
  height: 60px;
  z-index: 1;
}
</style>
